// Loại hàng và loại cont.
// TypeCargo của LOGI: DC · RF · OTH (TblFreightRoute / TblFreightRate / BOOKINGDATA).
// Board Mint-P2P tra theo 4 nhóm cont (CARGO / CONT trong script): DC · RF · NOR (reefer chạy khô) · OTH.
// Loại cont: TypeCont của BOOKINGDATA / TYPE của TblContainer + nhóm cont của board.

export const CARGO_TYPES = ['DC', 'RF', 'OTH'] as const;
export type CargoType = (typeof CARGO_TYPES)[number];

export function isCargoType(value: string): value is CargoType {
  return (CARGO_TYPES as readonly string[]).includes(value);
}

/** Nhóm cont trên màn tra P2P (thứ tự và nhãn theo board Mint-P2P). */
export const CONTAINER_CLASSES = [
  { key: 'DC', label: 'Hàng khô' },
  { key: 'RF', label: 'Hàng lạnh' },
  { key: 'NOR', label: 'Reefer chạy khô' },
  { key: 'OTH', label: 'Open top / Flat rack' },
] as const;
export type ContainerClass = (typeof CONTAINER_CLASSES)[number]['key'];

export interface ContainerType {
  code: string;
  size: 20 | 40;
  teu: 1 | 2;
  class: ContainerClass;
  highCube: boolean;
  label: string;
}

export const CONTAINER_TYPES: readonly ContainerType[] = [
  { code: '20DC', size: 20, teu: 1, class: 'DC', highCube: false, label: "20' Dry" },
  { code: '40DC', size: 40, teu: 2, class: 'DC', highCube: false, label: "40' Dry" },
  { code: '40HC', size: 40, teu: 2, class: 'DC', highCube: true, label: "40' High Cube" },
  { code: '20RF', size: 20, teu: 1, class: 'RF', highCube: false, label: "20' Reefer" },
  { code: '40RF', size: 40, teu: 2, class: 'RF', highCube: false, label: "40' Reefer" },
  { code: '20RH', size: 20, teu: 1, class: 'RF', highCube: true, label: "20' Reefer High" },
  { code: '40RH', size: 40, teu: 2, class: 'RF', highCube: true, label: "40' Reefer High" },
  { code: '20NOR', size: 20, teu: 1, class: 'NOR', highCube: false, label: "20' NOR" },
  { code: '40NOR', size: 40, teu: 2, class: 'NOR', highCube: false, label: "40' NOR" },
  { code: '20OT', size: 20, teu: 1, class: 'OTH', highCube: false, label: "20' Open Top" },
  { code: '20FR', size: 20, teu: 1, class: 'OTH', highCube: false, label: "20' Flat Rack" },
  { code: '40FR', size: 40, teu: 2, class: 'OTH', highCube: false, label: "40' Flat Rack" },
];

/** Cont hiện ở bộ chọn số lượng của từng nhóm, đúng thứ tự board (cont cuối là mặc định số lượng 1). */
export const CLASS_CONTAINERS: Readonly<Record<ContainerClass, readonly string[]>> = {
  DC: ['20DC', '40DC', '40HC'],
  RF: ['20RF', '40RH'],
  NOR: ['20NOR', '40NOR'],
  OTH: ['20OT', '40FR'],
};

export function defaultContainerOf(cls: ContainerClass): string {
  const list = CLASS_CONTAINERS[cls];
  return list[list.length - 1]!;
}

const ALIASES: Record<string, string> = {
  '20GP': '20DC', '20DV': '20DC', '20DRY': '20DC',
  '40GP': '40DC', '40DV': '40DC', '40DRY': '40DC',
  '40HQ': '40HC', '40HCDC': '40HC',
  '20RE': '20RF', '20RFR': '20RF',
  '40RE': '40RF', '40RFR': '40RF',
  '40HR': '40RH', '40RQ': '40RH', '40RHC': '40RH', '40HCRF': '40RH', '40REHC': '40RH',
  '20NR': '20NOR', '40NR': '40NOR',
  '20OPENTOP': '20OT', '20FLATRACK': '20FR', '40FLATRACK': '40FR',
};

const BY_CODE = new Map(CONTAINER_TYPES.map((c) => [c.code, c]));

/** Chuẩn hoá cách ghi loại cont (`40'HQ`, `40 hc`, `20GP`) → mã chuẩn; KGS / CBM / SHPT hay mã lạ thì `null`. */
export function normalizeContainer(value: string | null | undefined): string | null {
  if (!value) return null;
  const s = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const code = ALIASES[s] ?? s;
  return BY_CODE.has(code) ? code : null;
}

export function findContainer(value: string | null | undefined): ContainerType | undefined {
  const c = normalizeContainer(value);
  return c ? BY_CODE.get(c) : undefined;
}

/** Loại hàng LOGI của một loại cont. NOR chưa có quy ước TypeCargo trong LOGI → `null` (chờ anh chốt). */
export function cargoTypeOf(container: string): CargoType | null {
  const cls = findContainer(container)?.class;
  return cls === 'DC' || cls === 'RF' || cls === 'OTH' ? cls : null;
}

/** Tổng TEU của bộ số lượng `{ '40RH': 2, '20RF': 1 }`; mã không phải cont bị bỏ qua. */
export function totalTeu(qty: Readonly<Record<string, number>>): number {
  let teu = 0;
  for (const [code, n] of Object.entries(qty)) {
    const c = findContainer(code);
    if (c && n > 0) teu += c.teu * n;
  }
  return teu;
}
